import type { EmploymentType } from '@/constants/employmentTypes';

export type { EmploymentType } from '@/constants/employmentTypes';

export interface Lead {
  id: string;
  opportunityId?: string;
  companyId?: string;
  productId?: string; // link to products/{id}
  product?: ProductSnapshot; // denormalized snapshot for convenience
  productIds?: string[]; // Support for multiple products
  products?: ProductSnapshot[]; // Array of product snapshots
  value?: number; // optional deal value (derived from product)
  /** Anrede / Titel (z. B. Dr., Prof.) */
  title?: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone?: string;
  /** Zweite Telefonnummer */
  phoneSecondary?: string;
  /** Einwilligung Marketing (manuell dokumentiert) */
  consentMarketing?: boolean;
  consentMarketingRecordedAt?: Date;
  /** Datenschutzerklärung akzeptiert */
  consentPrivacyPolicy?: boolean;
  consentPrivacyPolicyRecordedAt?: Date;
  /** Double-Opt-in (E-Mail) bestätigt */
  doubleOptInConfirmed?: boolean;
  doubleOptInConfirmedAt?: Date;
  /** Kontakt als Rechnungsempfänger markieren */
  isInvoiceRecipient?: boolean;
  /** Kontakt als Empfänger von Reportings */
  isReportingRecipient?: boolean;
  /** Berechtigt für Freigaben in Marketingkonten des Kunden */
  marketingApprovalAuthorized?: boolean;
  /** Vertreter:in (Name) für Marketing-Freigaben, falls abweichend */
  marketingApprovalRepresentativeName?: string;
  language?: 'en' | 'de' | 'es' | 'fr' | 'it' | 'pt' | 'nl' | 'pl' | 'zh' | 'ja'; // Communication language
  // Built-in sources plus org-defined custom sources (see settingsService.customLeadSources).
  // `string & {}` keeps literal autocomplete while allowing custom values.
  source: 'website' | 'referral' | 'cold-outreach' | 'social' | 'partner' | (string & {});
  campaignId?: string;        // marketing attribution (e.g. Google Ads campaign that created this lead)
  campaignName?: string;      // denormalized campaign label for display
  stage: PipelineStage;
  score: number;
  createdAt: Date;
  updatedAt: Date;
  assignedTo?: string;
  nextFollowUp?: Date;
  companyData?: CompanyIntelligence;
  notes: Note[];
  activities: Activity[];
  automationData?: AutomationData;
  emails: Email[];
}

/** PATCH an Firestore: `null` entfernt optionale Zeitstempel-Felder */
export type LeadUpdateInput = Partial<Lead> & {
  consentMarketingRecordedAt?: Date | null;
  consentPrivacyPolicyRecordedAt?: Date | null;
  doubleOptInConfirmedAt?: Date | null;
};

// Create Contact type as alias for migration
export type Contact = Lead;

// Settings for organization
export interface OrganizationSettings {
  id: string;
  orgId: string;
  industries: string[];
  customLeadSources?: string[];
  contactStages?: string[];
  opportunityStages?: string[];
  defaultProducts?: string[];
  defaultLogoUrl?: string; // Default logo URL for offers/documents
  updatedAt: Date;
  updatedBy: string;
}

export interface Company {
  id: string;
  name: string;
  domain: string;
  industry: string;
  employeeCount: string;
  revenue: string;
  description: string;
  website: string;
  orgId?: string;
  phone?: string;
  /** Steuer-ID / USt-IdNr. (frei wählbar je nach Land) */
  taxId?: string;
  /** EU-USt-Id (optional, getrennt von nationaler Steuernummer) */
  vatId?: string;
  address?: {
    street: string;
    city: string;
    country: string;
    postalCode: string;
  };
  socialProfiles: Record<string, string>;
  productId?: string; // linked product
  product?: ProductSnapshot; // denormalized product data
  leadId?: string; // primary lead contact
  contactId?: string; // New field for contact reference
  lead?: { // denormalized lead snapshot (kept for backward compatibility)
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  contact?: { // New field for contact snapshot
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  members?: CompanyMember[];
  isCustomer?: boolean; // Flag to indicate if company is a customer
  customerSince?: Date;
  // Brand/Logo Information from website scraping
  branding?: {
    logoUrl?: string;
    brandColor?: string;
    accentColor?: string;
    tagline?: string;
    scrapedAt?: Date;
  };
  // Archive/Soft-delete fields
  archived?: boolean;
  archivedAt?: Date;
  archivedBy?: string; // userId who archived
  createdAt: Date;
  updatedAt: Date;
  // AI Enrichment data
  enrichment?: {
    description?: string;
    founded?: string;
    headquarters?: string;
    employees?: string;
    products?: string[];
    competitors?: string[];
    keyPeople?: {
      name: string;
      position: string;
    }[];
    recentNews?: {
      title: string;
      date: string;
      summary: string;
    }[];
    socialMedia?: {
      linkedin?: string;
      twitter?: string;
      facebook?: string;
    };
    businessModel?: string;
    targetMarket?: string;
    fundingInfo?: string;
    tags?: string[];
    lastEnriched?: Date;
    confidence?: 'high' | 'medium' | 'low';
  };
}

export interface CompanyMember {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  role?: string;
  department?: string;
  reportsToId?: string;
  isPrimaryContact?: boolean;
  isDecisionMaker?: boolean;
  notes?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// Product entity used to define per-product stage patterns for leads/opportunities
export interface Product {
  id: string;
  key: string; // e.g., "aimey", "consultancy"
  name: string;
  type: 'consultancy' | 'development' | 'solution' | 'service';
  /**
   * High-level service category that drives the pricing model + display.
   * Additive to `type` (which stays for backward compatibility with CRM
   * pipeline config, offers and opportunities). Derived from `type` for
   * legacy products that predate this field.
   */
  serviceCategory?: ServiceCategory;
  description?: string;
  orgId?: string;
  isDefault?: boolean;
  leadStages: string[]; // e.g., ['untouched', 'qualification', ...] per product
  opportunityStages: string[]; // e.g., ['prospecting', 'proposal', ...] per product
  pricing?: ProductPricing;
  createdAt: Date;
  updatedAt: Date;
}

export type ServiceCategory = 'managed_service' | 'consulting' | 'development';

/** A single rate-card tier (day rate per seniority level). */
export interface RateCardEntry {
  level: string;   // e.g. "Junior", "Consultant", "Senior", "Principal"
  dayRate: number; // per day, in the product currency
}

/** A fixed-price / turn-key package (development service). */
export interface FixedPricePackage {
  name: string;
  price: number;
  description?: string;
}

export interface ProductPricing {
  model: 'one_time' | 'recurring';
  currency?: string; // e.g., 'EUR'
  amount?: number; // exact/headline price when known
  range?: { min: number; max: number }; // pricing band if applicable
  billingPeriod?: 'month' | 'year' | 'quarter' | 'week';
  // --- Managed Service ---
  setupFee?: number; // one-time onboarding/setup fee
  // --- Consulting & Development (time & material) ---
  rateCards?: RateCardEntry[]; // day rates by level
  // --- Development (turn-key) ---
  fixedPackages?: FixedPricePackage[]; // optional fixed-price packages
}

/** Display config for the three service categories. */
export const SERVICE_CATEGORIES: { key: ServiceCategory; label: string; legacyType: Product['type'] }[] = [
  { key: 'managed_service', label: 'Managed Service', legacyType: 'service' },
  { key: 'consulting', label: 'Consulting', legacyType: 'consultancy' },
  { key: 'development', label: 'Development', legacyType: 'development' },
];

/** Default seniority levels for a fresh consulting/development rate card. */
export const DEFAULT_RATE_CARD_LEVELS = ['Junior', 'Consultant', 'Senior', 'Principal'];

export interface ProductSnapshot {
  id: string;
  key: string;
  name: string;
  type?: 'consultancy' | 'development' | 'solution' | 'service';
  serviceCategory?: ServiceCategory;
  leadStages?: string[];
  opportunityStages?: string[];
  pricing?: ProductPricing;
}

// Business Area types for Opportunity categorization
export type BusinessArea = 'consulting' | 'auto-boost' | 'performance' | 'creative' | 'other';

export const BUSINESS_AREAS: { key: BusinessArea; labelDe: string; labelEn: string }[] = [
  { key: 'consulting', labelDe: 'Beratung', labelEn: 'Consulting' },
  { key: 'auto-boost', labelDe: 'Auto-Boost', labelEn: 'Auto-Boost' },
  { key: 'performance', labelDe: 'Performance', labelEn: 'Performance' },
  { key: 'creative', labelDe: 'Kreativ', labelEn: 'Creative' },
  { key: 'other', labelDe: 'Sonstiges', labelEn: 'Other' },
];

export interface Opportunity {
  id: string;
  title: string;
  companyId: string;
  company: Company;
  productId?: string; // link to products/{id}
  product?: ProductSnapshot; // denormalized snapshot for convenience
  leadIds: string[];
  leads: Lead[];
  // Forward links into delivery/finance (deal-to-delivery chain)
  engagementId?: string;       // linked engagement/project created from this deal
  offerId?: string;            // linked offer/contract
  paymentScheduleId?: string;  // linked revenue/payment schedule
  stage: 'prospecting' | 'qualification' | 'proposal' | 'negotiation' | 'closing' | 'won' | 'lost';
  value?: number;
  valueSource?: 'product' | 'custom';
  billingTermMonths?: number; // for recurring product pricing calculation
  probability: number;
  expectedCloseDate: Date;
  actualCloseDate?: Date;
  assignedTo: string;
  assignedToName?: string;
  businessArea?: BusinessArea;
  createdAt: Date;
  updatedAt: Date;
  source: 'inbound' | 'outbound' | 'referral';
  priority: 'low' | 'medium' | 'high';
  lostReason?: string;
  nextFollowUp?: Date;
  summary: {
    totalLeads: number;
    averageScore: number;
    highestScore: number;
    contactPoints: number;
    lastActivity: Date;
    keyDecisionMakers: string[];
  };
  activities: OpportunityActivity[];
  notes: OpportunityNote[];
  meddpiccId?: string; // Link to MEDDPICC assessment (stored separately for performance)
}

export interface OpportunityActivity {
  id: string;
  type: 'lead_created' | 'lead_stage_changed' | 'email_sent' | 'email_received' | 'call_made' | 'meeting_scheduled' | 'proposal_sent' | 'stage_changed' | 'note_added';
  description: string;
  leadId?: string;
  leadName?: string;
  createdAt: Date;
  createdBy: string;
  metadata?: Record<string, any>;
}

export interface OpportunityNote {
  id: string;
  content: string;
  createdAt: Date;
  createdBy: string;
  type: 'general' | 'strategy' | 'concern' | 'competitive';
}

export interface CompanyIntelligence {
  domain: string;
  industry: string;
  employeeCount: string;
  revenue: string;
  technologies: string[];
  socialProfiles: Record<string, string>;
  description: string;
  keyPersons: string[];
}

export interface Note {
  id: string;
  content: string;
  createdAt: Date;
  createdBy: string;
  type: 'call' | 'email' | 'meeting' | 'general';
}

export interface Activity {
  id: string;
  type: 'email_sent' | 'email_opened' | 'call_made' | 'meeting_scheduled' | 'stage_changed';
  description: string;
  createdAt: Date;
  metadata?: Record<string, any>;
}

export interface TaskGroup {
  id: string;
  name: string;
  description?: string;
  color?: string; // Hex color for visual identification
  icon?: string; // Icon name or emoji
  orgId: string;
  createdBy: string;
  isDefault?: boolean; // Mark the default group
  order?: number; // For sorting groups
  createdAt: Date;
  updatedAt: Date;
}

export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export interface TaskAttachment {
  id: string;
  name: string;
  url: string;
  size?: number;
  mimeType?: string;
  uploadedAt: Date;
  uploadedBy?: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  leadId: string; // Will be changed to contactId in future
  contactId?: string; // New field for contacts
  opportunityId?: string;
  companyId?: string; // Optional company association
  productId?: string; // Optional product association
  groupId?: string; // Task group assignment
  engagementId?: string; // Engagement/project association
  milestoneId?: string; // Milestone within engagement
  isRecurring?: boolean; // For recurring tasks from engagements
  recurringTemplateId?: string; // Reference to recurring task template
  echoBrainProjectId?: string; // Linked Echo-Brain project (technical tasks)
  assignedTo: string;
  createdBy: string; // Who created the task
  visibility: 'private' | 'public'; // Task visibility
  orgId?: string; // Organization ID for public tasks
  dueDate: Date;
  startDate?: Date; // Explicit start date (for Gantt/timeline)
  completed: boolean;
  status?: 'pending' | 'in_progress' | 'completed' | 'blocked';
  /** Optional: ein Task, der diese Aufgabe (fachlich) blockiert – Link in der Übersicht */
  blockedByTaskId?: string;
  priority: 'low' | 'medium' | 'high';
  type: 'call' | 'email' | 'meeting' | 'follow_up' | 'presentation' | 'todo' | 'technical';
  tags?: string[]; // User-defined tags for categorization
  createdAt?: Date;
  updatedAt?: Date;
  completedAt?: Date;
  completedBy?: string;
  sharedWith?: string[]; // Optional: specific users to share with
  notes?: string; // Optional notes/comments
  kanbanColumn?: string; // Custom kanban column ID

  // --- Dependencies ---
  dependsOn?: string[]; // Task IDs that must be completed before this one

  // --- Sub-Tasks ---
  parentTaskId?: string; // If this is a sub-task: parent task ID
  subtaskIds?: string[]; // Direct child task IDs

  // --- Effort ---
  estimatedHours?: number; // Estimated hours for this task

  // --- Extended categorization ---
  taskCategory?: 'crm' | 'project'; // CRM tasks vs project tasks
  projectTaskType?: 'task' | 'bug' | 'feature' | 'deliverable' | 'review' | 'research';

  // --- Development module (Theme → Epic → User Story → Task) ---
  userStoryId?: string; // authoritative parent in the dev hierarchy
  devEpicId?: string;   // denormalized rollup helper
  devThemeId?: string;  // denormalized rollup helper + Aufgaben "Development" segment

  // --- Checklist ---
  checklist?: ChecklistItem[];

  // --- Attachments ---
  attachments?: TaskAttachment[];
}

export interface AIInsight {
  id: string;
  leadId: string;
  type: 'recommendation' | 'warning' | 'opportunity';
  title: string;
  description: string;
  confidence: number;
  actionable: boolean;
  suggestedActions: string[];
}

export type PipelineStage = 'untouched' | 'interested' | 'demo' | 'offer' | 'closing' | 'won' | 'lost';

export interface EmailTemplate {
  id: string;
  name: string;
  stage: PipelineStage;
  subject: string;
  content: string;
  variables: string[];
}

export interface AutomationData {
  researchData?: {
    companyInfo: {
      description: string;
      industry: string;
      size: string;
      recentNews: string[];
    };
    keyInsights: string[];
    nextBestAction: string;
  };
  emailSamples?: {
    id: string;
    subject: string;
    content: string;
    tone: string;
    approach: string;
  }[];
  selectedEmail?: string;
  callScript?: {
    greeting: string;
    keyTalkingPoints: string[];
    objectionHandling: string[];
    closingQuestions: string[];
  };
  meetingPrep?: {
    agenda: string[];
    talkingPoints: string[];
    invitationEmail: {
      subject: string;
      content: string;
    };
  };
  offerData?: {
    product: {
      name: string;
      description: string;
      features: string[];
    };
    pricing: {
      level: 'basic' | 'professional' | 'enterprise';
      price: string;
      discount?: string;
      terms: string;
    };
    proposal: {
      summary: string;
      valueProposition: string[];
      implementation: string[];
      timeline: string;
    };
    offerEmail: {
      subject: string;
      content: string;
    };
  };
  completedAt?: Date;
}

// Module Subscription (€19/Module System)
export interface ModuleSubscription {
  moduleId: string;
  moduleName: string;
  price: number;
  activatedAt: Date;
  status: 'trial' | 'active' | 'cancelled';
  trialEndsAt?: Date;  // 30 days from activatedAt for new customers
}

// Organization interface
export interface Organization {
  id: string;
  name: string;
  domain?: string;
  industry?: string;
  size?: 'small' | 'medium' | 'large' | 'enterprise';
  logo?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  phone?: string;
  email?: string;
  website?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // User ID who created the org
  settings?: {
    timezone?: string;
    language?: string;
    currency?: string;
  };
  subscription?: {
    plan: 'free' | 'starter' | 'professional' | 'enterprise';
    status: 'active' | 'suspended' | 'cancelled';
    validUntil?: Date;
    seats?: number;
  };
  
  // Module-Based Pricing (NEW)
  billingType?: 'legacy_pro' | 'module_based';  // Migration support
  modules?: ModuleSubscription[];  // Active modules
  totalMonthlyPrice?: number;  // Sum of active module prices
  
  // Onboarding Data (NEW)
  onboardingData?: {
    websiteUrl?: string;
    email?: string;
    phone?: string;
    scrapedAt?: Date;
    detectedIndustry?: string;
    industryConfidence?: number;
    appliedTemplate?: string;
    templateName?: string;
    selectedModules?: string[];
    onboardingCompletedAt?: Date;
    appliedAt?: Date;
    industry?: string;
  };
  
  // Demo organization fields
  isDemo?: boolean; // Whether this is a demo organization
  demoDataGenerated?: boolean; // Whether demo data has been generated
  demoCreatedAt?: Date; // When the demo was created
}

// Organization membership for users
export interface OrganizationMembership {
  orgId: string;
  orgName: string;
  role: 'admin' | 'manager' | 'sales_rep';
  joinedAt: Date;
  status: 'active' | 'invited' | 'suspended';
  permissions?: string[];
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'sales_rep'; // Default/primary role
  isSuperAdmin?: boolean; // System-wide super admin (can manage all orgs)
  customRoleId?: string; // Reference to custom role if assigned
  avatar?: string;
  position?: string;
  specialties: string[];
  currentLeadCount?: number;
  orgId?: string; // Current active organization
  organizations?: OrganizationMembership[]; // All organization memberships
  teamId?: string;
  status?: 'active' | 'inactive' | 'suspended';
  /** Kanonische Werte; ältere Docs können noch `internal`/`freelancer` enthalten (siehe `normalizeEmploymentType`) */
  employmentType?: EmploymentType;
  createdAt?: Date;
  updatedAt?: Date;
  lastActiveOrg?: string; // Last active organization ID
  isDemo?: boolean; // Whether this is a demo user (for demo organizations)
}

export interface Email {
  id: string;
  subject: string;
  content: string;
  direction: 'sent' | 'received';
  timestamp: Date;
  isRead: boolean;
  hasResponse: boolean;
  questions: EmailQuestion[];
  allQuestionsAnswered: boolean;
  needsFollowUp: boolean;
  followUpSuggestion?: string;
  threadId?: string;
}

export interface EmailQuestion {
  id: string;
  question: string;
  answered: boolean;
  answer?: string;
}

// External API Types
export interface ExternalContact {
  id: string;
  // Contact data from external source
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  position?: string;
  website?: string;
  notes?: string;
  customFields?: Record<string, any>;
  
  // Tracking & Source Information
  sourceCompanyId: string;      // ID of the company that sent this contact
  sourceCompanyName: string;     // Name of the company
  apiKeyId: string;             // API key used
  submittedAt: Date;            // When the contact was submitted
  sourceReference?: string;      // External reference ID from source company
  campaignId?: string;          // Campaign or source identifier
  
  // Processing status
  status: 'pending' | 'processed' | 'rejected' | 'duplicate';
  processedAt?: Date;
  processedBy?: string;
  linkedLeadId?: string;        // If converted to a lead
  rejectionReason?: string;
  
  // Organization
  orgId: string;
}

export interface ApiKey {
  id: string;
  key: string;                  // The actual API key (hashed)
  companyId: string;            // Company this key belongs to
  companyName: string;
  description?: string;
  createdAt: Date;
  createdBy: string;
  lastUsed?: Date;
  usageCount: number;
  rateLimit: number;            // Requests per hour
  isActive: boolean;
  expiresAt?: Date;
  permissions: string[];        // What this key can access
  ipWhitelist?: string[];       // Optional IP restrictions
  orgId: string;
}

export interface ApiUsageLog {
  id: string;
  apiKeyId: string;
  companyId: string;
  endpoint: string;
  method: string;
  statusCode: number;
  requestBody?: any;
  responseTime: number;
  ipAddress: string;
  userAgent?: string;
  timestamp: Date;
  success: boolean;
  error?: string;
}

// Material Types
export interface Material {
  id: string;
  type: 'use_case' | 'case_study' | 'reference' | 'presentation' | 'video' | 'image' | 'brochure' | 'whitepaper' | 'link';
  title: string;
  description: string;
  attachments: MaterialAttachment[];
  generatedContent?: {
    linkedIn?: string;
    newsletter?: string;
  };
  createdBy: string;
  createdByName: string;
  orgId: string;
  createdAt: Date;
  updatedAt: Date;
  tags?: string[];
  status: 'draft' | 'published' | 'archived';
  comments?: MaterialComment[];
}

export interface MaterialComment {
  id: string;
  materialId: string;
  userId: string;
  userName: string;
  userEmail?: string;
  comment: string;
  createdAt: Date;
  updatedAt?: Date;
  edited?: boolean;
}

export interface MaterialAttachment {
  id: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: Date;
  extractedText?: string; // Text extracted from PDF for AI processing
  thumbnailUrl?: string; // Preview thumbnail for the file
  duration?: number; // For videos, duration in seconds
  dimensions?: { width: number; height: number }; // For images and videos
  isExternalLink?: boolean; // True if this is an external URL
  linkMetadata?: { // Metadata for external links
    title?: string;
    description?: string;
    siteName?: string;
    imageUrl?: string;
  };
}

// Content Template Types
export interface ContentTemplate {
  id: string;
  name: string;
  type: 'linkedIn' | 'newsletter';
  template: string; // Template with placeholders like {{title}}, {{description}}, etc.
  systemPrompt?: string; // Optional custom system prompt
  isDefault: boolean;
  orgId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// Offer Template Types
export interface OfferTemplate {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  orgId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  
  // Company Header Information (Seller Info)
  seller: {
    name: string;
    headline?: string; // Custom headline for the offer
    logo?: string;
    address: {
      street: string;
      postalCode: string;
      city: string;
      country: string;
    };
    taxId?: string;
    vatId?: string;
    phone?: string;
    email?: string;
    website?: string;
    bankDetails?: {
      bankName: string;
      iban: string;
      bic: string;
    };
  };
  
  // Default settings
  currency?: 'EUR' | 'USD' | 'GBP';
  paymentTerms?: string;
  termsAndConditions?: string;
  smallBusiness?: boolean;
  reverseCharge?: boolean;
}

// Scheduled Post Types
export interface ScheduledPost {
  id: string;
  materialId: string;
  material?: Material; // Populated for display
  contentType: 'linkedIn' | 'newsletter';
  content: string;
  scheduledFor: Date;
  status: 'pending' | 'published' | 'failed' | 'cancelled';
  platform?: string; // e.g., 'linkedin', 'email'
  publishedAt?: Date;
  error?: string;
  orgId: string;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
}

// Payment Schedule Types for Customer Revenue Management
export interface PaymentInstallment {
  month: string; // YYYY-MM
  amount: number;
  isPaid?: boolean;
  paidDate?: Date;
}

export interface PaymentSchedule {
  id: string;
  sourceOpportunityId?: string; // Link to won opportunity that created this
  revenueType: 'recurring' | 'one_time';
  totalContractValue: number;
  productId?: string;
  productName?: string;
  
  // Installment structure
  installments: PaymentInstallment[];
  
  // Metadata
  startMonth: string; // YYYY-MM
  endMonth?: string; // YYYY-MM (for one-time with installments or recurring with end date)
  isActive: boolean;
  createdAt: Date;
  createdBy?: string; // User ID or 'migration' for migrated data
  notes?: string;
}

// Extended Company type for Customer Management
export interface CustomerCompany extends Company {
  isCustomer: boolean;
  customerSince?: Date;
  monthlyRevenue?: Record<string, number>; // Keep for manual overrides
  paymentSchedules?: PaymentSchedule[]; // NEW: Structured payment schedules
  satisfaction?: number;
  contractEnd?: Date;
  renewalLikelihood?: number;
}

// Sorting field types for type safety
export type SortField = 'name' | 'company' | 'stage' | 'score' | 'source' | 'updatedAt' | 'assignedTo' | 'totalRevenue' | 'dealsCount' | 'avgDeal' | 'satisfaction' | 'renewalLikelihood';
export type SortDirection = 'asc' | 'desc';

// Offer Builder Types
export interface OfferItem {
  id: string;
  productId: string;
  product: ProductSnapshot;
  quantity: number;
  unitPrice: number;
  discount?: number; // percentage
  description?: string;
  deliveryTimeframe?: string; // e.g., "2-4 weeks"
  customizations?: string;
  /** Unit label for the quantity, e.g. "Tag(e)", "Monat", "Pauschal". */
  unit?: string;
  /** Which product pricing component this line was derived from. */
  pricingComponent?: 'recurring' | 'setup' | 'day_rate' | 'package' | 'fixed' | 'custom';
}

export interface Offer {
  id: string;
  offerNumber: string; // e.g., "OFF-2025-001" or "RE-2025-001" for invoices
  title: string;
  type: 'offer' | 'invoice' | 'contract'; // Angebot, Rechnung oder Vertrag
  
  // Client Information
  companyId?: string;
  company?: Company;
  opportunityId?: string;
  opportunity?: Opportunity;
  engagementId?: string; // linked engagement/project (deal-to-delivery chain)
  contactId?: string; // Lead/Contact ID
  contact?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    company: string;
  };
  
  // Deutsche Pflichtangaben (§14 UStG)
  seller: {
    name: string; // Vollständiger Name des Unternehmens
    headline?: string; // Custom headline/motto for the offer
    logo?: string; // Logo URL from Firebase Storage
    address: {
      street: string;
      postalCode: string;
      city: string;
      country: string;
    };
    taxId?: string; // Steuernummer
    vatId?: string; // Umsatzsteuer-Identifikationsnummer (USt-IdNr.)
    phone?: string;
    email?: string;
    website?: string;
    bankDetails?: {
      bankName: string;
      iban: string;
      bic: string;
    };
  };
  
  buyer: {
    name: string; // Vollständiger Name des Leistungsempfängers
    address: {
      street: string;
      postalCode: string;
      city: string;
      country: string;
    };
    taxId?: string;
    vatId?: string; // Bei B2B innerhalb EU
  };
  
  // Rechnungsdaten
  invoiceDate?: Date; // Ausstellungsdatum (Pflicht für Rechnung)
  deliveryDate?: Date; // Lieferdatum (Pflicht für Rechnung)
  items: OfferItem[];
  currency: 'EUR' | 'USD' | 'GBP'; // EUR ist Standard in Deutschland
  
  // Pricing
  subtotal: number; // Nettobetrag
  discount: number; // absolute value
  discountPercentage?: number;
  netTotal: number; // Gesamtnettobetrag (nach Rabatt)
  
  // USt-Berechnung (Deutsche Mehrwertsteuer)
  vatBreakdown?: {
    rate: number; // z.B. 19 oder 7
    netAmount: number;
    vatAmount: number;
  }[];
  totalVat: number; // Gesamtumsatzsteuer
  
  total: number; // Bruttobetrag (Gesamtbetrag inkl. USt)
  
  // Reverse Charge / Kleinunternehmer
  reverseCharge?: boolean; // Reverse-Charge-Verfahren (§13b UStG)
  smallBusiness?: boolean; // Kleinunternehmerregelung (§19 UStG)
  reverseChargeNote?: string; // Text für Reverse-Charge
  
  // Terms
  validUntil: Date; // Bei Angebot
  paymentTerms: string; // e.g., "Zahlbar innerhalb 14 Tagen netto"
  paymentDueDate?: Date; // Fälligkeitsdatum (für Rechnung)
  deliveryTerms?: string;
  
  // Content
  introduction?: string; // AI-generated or manual
  summary?: string;
  termsAndConditions?: string;
  notes?: string; // Interne Notizen (nicht auf PDF)
  
  // Contract details (only for type === 'contract')
  contractDetails?: import('./contract').ContractDetails;
  
  // Status
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'paid' | 'overdue' | 'active' | 'terminated' | 'renewed' | 'under_review' | 'pending_signature';
  sentAt?: Date;
  viewedAt?: Date;
  acceptedAt?: Date;
  rejectedAt?: Date;
  paidAt?: Date;
  
  // Metadata
  createdBy: string;
  createdByName: string;
  orgId: string;
  createdAt: Date;
  updatedAt: Date;
  
  // PDF
  pdfUrl?: string;
  pdfGeneratedAt?: Date;
}

// Business Plan Types
export interface CostSubcategory {
  id: string;
  name: string;
  monthlyAmount: number;
  isRecurring: boolean;
}

export interface CostCategory {
  id: string;
  name: string;
  subcategories: CostSubcategory[];
  order: number;
}

export interface BusinessPlan {
  id: string;
  orgId: string;
  name: string;
  
  // Cost Structure
  costs: {
    personnel: CostSubcategory[];      // Gehälter, Benefits, etc.
    operations: CostSubcategory[];     // Miete, Utilities, etc.
    marketing: CostSubcategory[];      // Ads, Events, Tools
    technology: CostSubcategory[];     // Software, Hardware, Cloud
    other: CostSubcategory[];
  };
  
  // Configuration
  timeHorizon: 12 | 24 | 36;
  baseMonth: string; // YYYY-MM format
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface MonthlyForecast {
  month: string; // YYYY-MM
  
  // Revenues
  recurringRevenue: number;           // From existing customers
  newRevenue: number;                 // From pipeline
  totalRevenue: number;
  revenueByProduct: Record<string, number>;
  
  // Costs
  totalCosts: number;
  costsByCategory: Record<string, number>;
  
  // Metrics
  profit: number;
  profitMargin: number;
  cumulativeProfit: number;
}

export interface ForecastSummary {
  totalRevenue: number;
  totalCosts: number;
  totalProfit: number;
  avgMonthlyRevenue: number;
  avgMonthlyCosts: number;
  breakEvenMonth?: string;
  roi: number;
}

export interface BusinessForecast {
  scenario: 'conservative' | 'standard' | 'progressive';
  months: MonthlyForecast[];
  summary: ForecastSummary;
}

// AI Business Insights Types
export interface AIBusinessInsights {
  summary: string;                        // Gesamtbewertung
  riskScore: number;                      // 0-100 Risk Level
  
  weaknesses: {
    type: 'revenue' | 'costs' | 'pipeline' | 'customer' | 'product';
    severity: 'critical' | 'high' | 'medium' | 'low';
    description: string;
    impact: string;                       // "Could reduce revenue by 20%"
    recommendation: string;
  }[];
  
  opportunities: {
    type: 'upsell' | 'cross_sell' | 'new_market' | 'pricing' | 'efficiency';
    potential: number;                    // Expected revenue increase
    description: string;
    actionSteps: string[];
    priority: number;
  }[];
  
  warnings: {
    type: 'cashflow' | 'churn' | 'pipeline' | 'dependency';
    description: string;
    suggestedAction: string;
    deadline?: string;                    // When to act
  }[];
  
  comparisons: {
    metric: string;
    yourValue: number;
    industryBenchmark?: number;
    status: 'above' | 'below' | 'on_par';
  }[];
}

// Time Tracking Types (BAG/EU Compliance)
export interface TimeEntry {
  id: string;
  userId: string;
  userName: string;
  orgId: string;
  
  // Time tracking (BAG compliance)
  startTime: Date;
  endTime?: Date;
  duration?: number; // minutes, net working time (after break deduction)
  breakMinutes?: number; // minutes of break, deducted from gross duration
  date: string; // YYYY-MM-DD for easy filtering
  
  // Optional associations
  taskId?: string;
  task?: { id: string; title: string }; // denormalized snapshot
  opportunityId?: string;
  opportunity?: { id: string; title: string }; // denormalized snapshot
  companyId?: string;
  company?: { id: string; name: string }; // denormalized snapshot
  engagementId?: string;
  engagement?: { id: string; name: string; type: string }; // denormalized snapshot
  milestoneId?: string;
  milestone?: { id: string; title: string }; // denormalized snapshot
  
  // Description & categorization
  description?: string;
  category?: 'consulting' | 'documentation' | 'development' | 'growth' | 'training' | 'onboarding' | 'sick' | 'vacation' | 'research' | 'admin' | 'project_task' | 'dress_time' | 'remote_work';
  /** Mehrere Arbeits-Kategorien; `category` bleibt die erste (Abfragen/Legacy). */
  categories?: Array<
    | 'consulting'
    | 'documentation'
    | 'development'
    | 'growth'
    | 'training'
    | 'onboarding'
    | 'sick'
    | 'vacation'
    | 'research'
    | 'admin'
    | 'project_task'
    | 'dress_time'
    | 'remote_work'
  >;
  workLocation?: string;
  availabilityStatus?: 'available' | 'not_available_for_client_visits';
  availabilityNote?: string;
  
  // Pause tracking
  pausedAt?: Date;
  pausedDuration?: number; // total paused time in seconds

  // Status & metadata
  status: 'running' | 'paused' | 'completed';
  isOvertime?: boolean;
  isWeekend?: boolean;
  isHoliday?: boolean;
  
  // GDPR & audit trail
  createdAt: Date;
  updatedAt: Date;
  editedBy?: string; // Track if manager edited
  editHistory?: Array<{
    editedAt: Date;
    editedBy: string;
    field: string;
    oldValue: any;
    newValue: any;
  }>;
  
  // Notes
  notes?: string;
}

// Customer Intelligence Types (Master Record)
export * from './customerIntelligence';

// Vehicle Sync Types
export * from './syncConfig';

// Merchant API Types
export * from './merchantApi';

// Expense / Reisekostenabrechnung Types
export * from './expense';

// Team Skills Types
export * from './teamSkills';

// ============================================================================
// USER FEEDBACK TYPES
// ============================================================================
export type FeedbackType = 'bug' | 'change_request' | 'feature_request';
export type FeedbackPriority = 'low' | 'medium' | 'high';
export type FeedbackStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type FeedbackCategory = 'ui' | 'performance' | 'functionality' | 'data' | 'integration' | 'ai_agents' | 'other';

export interface FeedbackAssignee {
  userId: string;
  userName: string;
  assignedAt: Date;
}

export interface UserFeedback {
  id: string;
  orgId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  type: FeedbackType;
  category: FeedbackCategory;
  title: string;
  description: string;
  priority: FeedbackPriority;
  status: FeedbackStatus;
  currentPage: string;
  appSection: string;
  screenshotUrl?: string;
  browserInfo: string;
  appVersion?: string;
  createdAt: Date;
  updatedAt?: Date;
  adminNotes?: string;
  upvotes?: string[];
  assignees?: FeedbackAssignee[];
  workNotes?: string;
  testedVerified?: boolean;
  testedBy?: string;
  testedAt?: Date;
  resolvedAt?: Date;
}

// ─── Company Data Cleanup Types ──────────────────────────────────────

export interface CompanyDependencies {
  opportunities: { total: number; open: number; won: number; items: Array<{ id: string; title: string; stage: string }> };
  leads: { total: number; items: Array<{ id: string; name: string }> };
  tasks: { total: number; open: number };
  timeEntries: number;
  engagements: number;
  expenses: { total: number; amount: number };
  customerIntelligence: number;
  hasActiveContracts: boolean;
  hasPaymentSchedules: boolean;
  canSafelyDelete: boolean; // true if no open opportunities, no active contracts
}

export type CompanyAuditAction = 'merge' | 'archive' | 'unarchive' | 'delete';

export interface CompanyAuditEntry {
  id?: string;
  action: CompanyAuditAction;
  performedBy: string; // userId
  performedByName?: string;
  performedAt: Date;
  orgId: string;
  primaryCompanyId?: string;
  secondaryCompanyId?: string;
  companyName: string;
  secondaryCompanyName?: string;
  details: Record<string, any>; // field changes, counts, etc.
}

export type RevenueMergeStrategy = 'add' | 'primary' | 'pick';

export interface DuplicateGroup {
  companies: Company[];
  reason: 'similar_name' | 'same_domain' | 'both';
  similarity: number; // 0-1 score
}
